type TelemetryLog = (
  event: string,
  detail?: Record<string, string | number | boolean | null>
) => void;

type TelemetryFlush = () => void;

type SessionEvent = {
  event?: unknown;
  state?: unknown;
};

type ConnectionMetadata = {
  reason?: unknown;
  status?: unknown;
  reconnectCount?: unknown;
  backoffMs?: unknown;
};

const SESSION_STATES = new Set([
  "open",
  "recording",
  "closing",
  "finalising",
  "complete",
  "failed",
]);
const WRAP_UP_STATES = new Set(["closing", "finalising", "complete", "failed"]);
const CONNECTION_FAILURE_REASONS = new Set([
  "http_error",
  "network_error",
  "stream_ended",
  "authorization_revoked",
]);
const MAX_RECONNECT_COUNT = 10000;
const MAX_BACKOFF_MS = 30000;

function sessionState(value: unknown): string | null {
  return typeof value === "string" && SESSION_STATES.has(value) ? value : null;
}

function boundedInteger(value: unknown, max: number): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? Math.min(value, max)
    : null;
}

/**
 * Report the client's SSE observations once per connection or state transition.
 *
 * The session feed has no replay buffer, but its initial `connected` event reports the
 * current state after every reconnect. Treating that state as the new baseline prevents
 * a delayed or replayed `state_change` for the same value from being counted twice.
 */
export function createSessionEventTelemetry(log: TelemetryLog, flush: TelemetryFlush) {
  let successfulConnections = 0;
  let lastSseState: string | null = null;
  let failureReported = false;
  let resultReadyReported = false;

  function onEvent(event: SessionEvent, metadata?: ConnectionMetadata): void {
    if (event.event === "connected") {
      const state = sessionState(event.state);
      if (successfulConnections === 0) {
        log("sse_connected", { state, reconnect_count: 0 });
      } else {
        const reconnectCount =
          boundedInteger(metadata?.reconnectCount, MAX_RECONNECT_COUNT) ??
          Math.min(successfulConnections, MAX_RECONNECT_COUNT);
        const backoffMs = boundedInteger(metadata?.backoffMs, MAX_BACKOFF_MS);
        log("sse_reconnected", { state, reconnect_count: reconnectCount, backoff_ms: backoffMs });
      }
      successfulConnections = Math.min(successfulConnections + 1, MAX_RECONNECT_COUNT + 1);
      lastSseState = state;
      failureReported = false;
      return;
    }

    if (event.event === "state_change") {
      const state = sessionState(event.state);
      if (!state || state === lastSseState) return;
      lastSseState = state;
      log("sse_state_change_received", { state });
      if (WRAP_UP_STATES.has(state)) flush();
      return;
    }

    if (event.event === "result_ready" && !resultReadyReported) {
      resultReadyReported = true;
      log("sse_result_ready_received");
      flush();
    }
  }

  function onConnectionChange(connected: boolean, metadata?: ConnectionMetadata): void {
    if (connected || metadata?.reason === "aborted" || failureReported) return;
    const reason =
      typeof metadata?.reason === "string" && CONNECTION_FAILURE_REASONS.has(metadata.reason)
        ? metadata.reason
        : "network_error";
    const reconnectCount = Math.min(Math.max(successfulConnections - 1, 0), MAX_RECONNECT_COUNT);
    const detail: Record<string, string | number> = { reason, reconnect_count: reconnectCount };
    if (
      typeof metadata?.status === "number" &&
      Number.isInteger(metadata.status) &&
      metadata.status >= 100 &&
      metadata.status <= 599
    ) {
      detail.status = metadata.status;
    }
    log("sse_error", detail);
    failureReported = true;
  }

  return { onEvent, onConnectionChange };
}
